import {
  TIoTAlertDevice,
  TIoTAlertEntry,
  TIoTAlertResult,
  TIoTAlertStatus,
} from '@treedis/tour-loader';

/**
 * Verifies if a device's value is within limits and returns appropriate status and message
 */
export function verifyLimit(
  info: TIoTAlertDevice,
  ThingName: string,
): { status: TIoTAlertStatus; message: string } {
  const {
    NumberValue,
    ResponseStatus,
    AlarmUpperLimit,
    AlarmLowerLimit,
    WarningUpperLimit,
    WarningLowerLimit,
  } = info;

  const valueStr = typeof NumberValue === 'number' ? NumberValue : 'unknown';

  if (ResponseStatus === 'bad') {
    return {
      status: 'alarm',
      message: `Alarm: [${ThingName}] is in a critical state.`,
    };
  }
  if (ResponseStatus === 'warning') {
    return {
      status: 'warning',
      message: `Warning: [${ThingName}] is in a warning state.`,
    };
  }
  if (ResponseStatus === 'good') {
    return {
      status: 'default',
      message: `[${ThingName}] is operating within normal parameters.`,
    };
  }

  if (typeof AlarmUpperLimit === 'number' && NumberValue > AlarmUpperLimit) {
    return {
      status: 'alarm',
      message: `Alarm: [${ThingName}] has exceeded its upper limit (${valueStr} > ${AlarmUpperLimit})`,
    };
  }
  if (typeof AlarmLowerLimit === 'number' && NumberValue < AlarmLowerLimit) {
    return {
      status: 'alarm',
      message: `Alarm: [${ThingName}] has fallen below its lower limit (${valueStr} < ${AlarmLowerLimit})`,
    };
  }
  if (typeof WarningUpperLimit === 'number' && NumberValue > WarningUpperLimit) {
    return {
      status: 'warning',
      message: `Warning: [${ThingName}] is approaching/exceeding its warning upper limit (${valueStr} > ${WarningUpperLimit})`,
    };
  }
  if (typeof WarningLowerLimit === 'number' && NumberValue < WarningLowerLimit) {
    return {
      status: 'warning',
      message: `Warning: [${ThingName}] is approaching/falling below its warning lower limit (${valueStr} < ${WarningLowerLimit})`,
    };
  }

  return {
    status: 'default',
    message: `[${ThingName}] is operating within normal parameters.`,
  };
}


/**
 * Manages device alerts in localStorage
 */
export const handleLocalStorageAlerts = (
  deviceId: string | number,
  newMessage: string,
  newStatus: TIoTAlertStatus,
): TIoTAlertResult => {
  let alerts: TIoTAlertEntry[] = [];
  const stored = localStorage.getItem('iotAlert');
  if (stored) {
    try {
      alerts = JSON.parse(stored);
      if (!Array.isArray(alerts)) alerts = [];
    } catch {
      alerts = [];
    }
  }

  const existing = alerts.find((entry) => entry.deviceId === String(deviceId));

  if (existing) {
    if (existing.lastStatus === newStatus) {
      return { shouldNotify: false, isNewAlert: false };
    } else {
      existing.lastStatus = newStatus;
      existing.message = newMessage;
      existing.triggered = false;
      localStorage.setItem('iotAlert', JSON.stringify(alerts));
      return { shouldNotify: true, isNewAlert: false };
    }
  } else {
    alerts.push({
      deviceId: String(deviceId),
      lastStatus: newStatus,
      message: newMessage,
      triggered: false,
    });
    localStorage.setItem('iotAlert', JSON.stringify(alerts));
    return { shouldNotify: newStatus !== 'default', isNewAlert: true };
  }
};

/**
 * Marks an alert as triggered in localStorage
 */
export const markAlertAsTriggered = (deviceId: string | number): void => {
  const stored = localStorage.getItem('iotAlert');
  if (stored) {
    try {
      const alerts = JSON.parse(stored);
      const alertToUpdate = alerts.find(
        (alert: TIoTAlertEntry) => alert.deviceId === String(deviceId),
      );
      if (alertToUpdate) {
        alertToUpdate.triggered = true;
        localStorage.setItem('iotAlert', JSON.stringify(alerts));
      }
    } catch (error) {
      console.error('Error updating alert triggered status:', error);
    }
  }
};
